import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CallType, RTCIceServerConfig } from './types';


interface PeerCallOptions {
  iceServers: RTCIceServerConfig[];
  sendSignal: (event: Record<string, unknown>) => boolean;
  onConnected: () => void;
  onFailure: (reason: string) => void;
}


export function usePeerCall({ iceServers, sendSignal, onConnected, onFailure }: PeerCallOptions) {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const pendingIceRef = useRef<RTCIceCandidateInit[]>([]);
  const cleanedRef = useRef(true);
  const connectedReportedRef = useRef(false);
  const activeCallIdRef = useRef<string | null>(null);

  const cleanup = useCallback(() => {
    if (cleanedRef.current) return;
    cleanedRef.current = true;
    localStreamRef.current?.getTracks().forEach(track => track.stop());
    localStreamRef.current = null;
    remoteStreamRef.current = null;
    pendingIceRef.current = [];
    activeCallIdRef.current = null;
    connectedReportedRef.current = false;
    const peer = peerRef.current;
    peerRef.current = null;
    peer?.close();
    setLocalStream(null);
    setRemoteStream(null);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const prepareMedia = useCallback(async (callType: CallType) => {
    cleanup();
    cleanedRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: callType === 'video',
      });
      if (cleanedRef.current) {
        stream.getTracks().forEach(track => track.stop());
        throw new Error('Media preparation was cancelled');
      }
      localStreamRef.current = stream;
      setLocalStream(stream);
    } catch (error) {
      cleanup();
      throw error;
    }
  }, [cleanup]);

  const ensurePeer = useCallback((callId: string): RTCPeerConnection => {
    if (peerRef.current) {
      if (activeCallIdRef.current !== callId) throw new Error('Peer belongs to another call');
      return peerRef.current;
    }
    if (!localStreamRef.current) throw new Error('Local media is not prepared');

    activeCallIdRef.current = callId;
    connectedReportedRef.current = false;
    const peer = new RTCPeerConnection({ iceServers });
    peerRef.current = peer;
    localStreamRef.current.getTracks().forEach(track => {
      peer.addTrack(track, localStreamRef.current!);
    });

    const aggregate = new MediaStream();
    remoteStreamRef.current = aggregate;
    setRemoteStream(aggregate);
    peer.ontrack = event => {
      const supplied = event.streams[0];
      if (supplied) {
        remoteStreamRef.current = supplied;
        setRemoteStream(supplied);
        return;
      }
      if (remoteStreamRef.current) remoteStreamRef.current.addTrack(event.track);
      setRemoteStream(remoteStreamRef.current);
    };
    peer.onicecandidate = event => {
      if (!event.candidate || !activeCallIdRef.current) return;
      sendSignal({
        type: 'signal.ice',
        call_id: activeCallIdRef.current,
        candidate: event.candidate.toJSON(),
      });
    };
    peer.onconnectionstatechange = () => {
      if (peer.connectionState === 'connected' && !connectedReportedRef.current) {
        connectedReportedRef.current = true;
        onConnected();
      } else if (peer.connectionState === 'failed') {
        onFailure('ice_failed');
      }
    };
    return peer;
  }, [iceServers, onConnected, onFailure, sendSignal]);

  const flushPendingIce = useCallback(async (peer: RTCPeerConnection) => {
    const pending = pendingIceRef.current;
    pendingIceRef.current = [];
    for (const candidate of pending) await peer.addIceCandidate(candidate);
  }, []);

  const createOffer = useCallback(async (callId: string) => {
    const peer = ensurePeer(callId);
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    sendSignal({ type: 'signal.offer', call_id: callId, sdp: offer.sdp ?? '' });
  }, [ensurePeer, sendSignal]);

  const handleOffer = useCallback(async (callId: string, sdp: string) => {
    const peer = ensurePeer(callId);
    await peer.setRemoteDescription({ type: 'offer', sdp });
    await flushPendingIce(peer);
    const answer = await peer.createAnswer();
    await peer.setLocalDescription(answer);
    sendSignal({ type: 'signal.answer', call_id: callId, sdp: answer.sdp ?? '' });
  }, [ensurePeer, flushPendingIce, sendSignal]);

  const handleAnswer = useCallback(async (callId: string, sdp: string) => {
    const peer = ensurePeer(callId);
    await peer.setRemoteDescription({ type: 'answer', sdp });
    await flushPendingIce(peer);
  }, [ensurePeer, flushPendingIce]);

  const handleIce = useCallback(async (callId: string, candidate: RTCIceCandidateInit) => {
    const peer = ensurePeer(callId);
    if (!peer.remoteDescription) {
      pendingIceRef.current.push(candidate);
      return;
    }
    await peer.addIceCandidate(candidate);
  }, [ensurePeer]);

  const setMuted = useCallback((muted: boolean) => {
    localStreamRef.current?.getAudioTracks().forEach(track => { track.enabled = !muted; });
  }, []);

  const setCameraOff = useCallback((cameraOff: boolean) => {
    localStreamRef.current?.getVideoTracks().forEach(track => { track.enabled = !cameraOff; });
  }, []);

  return useMemo(() => ({
    localStream,
    remoteStream,
    prepareOutgoing: prepareMedia,
    prepareIncoming: prepareMedia,
    createOffer,
    handleOffer,
    handleAnswer,
    handleIce,
    setMuted,
    setCameraOff,
    cleanup,
  }), [
    cleanup, createOffer, handleAnswer, handleIce, handleOffer, localStream,
    prepareMedia, remoteStream, setCameraOff, setMuted,
  ]);
}
